import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { AppHeader } from "@/components/AppHeader";
import {
  ArrowLeft,
  Trophy,
  Target,
  Flame,
  ClipboardList,
  Loader2,
  TrendingUp,
  BookOpen,
} from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const scoreColor = (pct) => {
  if (pct >= 80) return "hsl(var(--brimi-mint))";
  if (pct >= 50) return "hsl(var(--brimi-blue))";
  return "hsl(var(--brimi-coral))";
};

const StatCard = ({ Icon, label, value, suffix, color, testId }) => (
  <div className="tactile-card p-5" data-testid={testId}>
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
      style={{ background: `${color}20`, color }}
    >
      <Icon className="w-5 h-5" />
    </div>
    <div className="text-3xl font-black tracking-tight">
      {value}
      {suffix && (
        <span className="text-base text-muted-foreground font-bold ml-0.5">
          {suffix}
        </span>
      )}
    </div>
    <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mt-1">
      {label}
    </div>
  </div>
);

export default function ProgressPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/quiz/stats")
      .then((r) => {
        if (!cancelled) setStats(r.data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e?.response?.data?.detail || "Couldn't load progress");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-6 py-16 text-center">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-[hsl(var(--brimi-blue))]" />
          <p className="mt-4 text-muted-foreground">Loading your progress…</p>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-2xl font-black mb-3">Couldn&apos;t load progress</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Link
            to="/subjects"
            className="pill-btn bg-[hsl(var(--brimi-blue))] text-white px-6 py-3"
          >
            Back to subjects
          </Link>
        </main>
      </div>
    );
  }

  const isEmpty = !stats || stats.total_attempts === 0;

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main
        data-testid="progress-page"
        className="max-w-4xl mx-auto px-6 py-10"
      >
        <button
          onClick={() => navigate(-1)}
          data-testid="progress-back-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-5"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="mb-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[hsl(var(--brimi-yellow))]/30 border border-[hsl(var(--brimi-yellow))]/50 mb-3">
            <Flame className="w-3.5 h-3.5 text-[hsl(var(--brimi-coral))]" />
            <span className="text-xs font-bold uppercase tracking-wider">
              Your progress
            </span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight">
            Quiz history &amp;{" "}
            <span className="relative inline-block">
              <span className="relative z-10">stats</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-[hsl(var(--brimi-mint))]/60 -z-0 rounded-sm" />
            </span>
          </h1>
        </div>

        {isEmpty ? (
          <div
            className="tactile-card p-10 text-center"
            data-testid="progress-empty-state"
          >
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-[hsl(var(--brimi-blue))]/10 text-[hsl(var(--brimi-blue))] flex items-center justify-center">
              <ClipboardList className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-black mb-2">
              No quiz attempts yet
            </h2>
            <p className="text-muted-foreground text-sm mb-6 max-w-sm mx-auto">
              Take a quiz on any chapter and we&apos;ll start tracking your scores,
              streaks and weak topics here.
            </p>
            <Link
              to="/subjects"
              data-testid="progress-start-quiz-btn"
              className="pill-btn bg-[hsl(var(--brimi-blue))] text-white px-6 py-3"
            >
              Pick a chapter
            </Link>
          </div>
        ) : (
          <>
            {/* Top-level stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              <StatCard
                Icon={ClipboardList}
                label="Quizzes taken"
                value={stats.total_attempts}
                color="hsl(var(--brimi-blue))"
                testId="stat-total"
              />
              <StatCard
                Icon={Target}
                label="Average score"
                value={stats.avg_score}
                suffix="%"
                color="hsl(var(--brimi-coral))"
                testId="stat-avg"
              />
              <StatCard
                Icon={Trophy}
                label="Best score"
                value={stats.best_score}
                suffix="%"
                color="hsl(var(--brimi-yellow))"
                testId="stat-best"
              />
              <StatCard
                Icon={TrendingUp}
                label="Correct answers"
                value={`${stats.total_correct}/${stats.total_questions_answered}`}
                color="hsl(var(--brimi-mint))"
                testId="stat-correct"
              />
            </div>

            {/* By subject */}
            {stats.by_subject?.length > 0 && (
              <section className="mb-10" data-testid="by-subject-section">
                <h2 className="text-xl font-black mb-4">By subject</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {stats.by_subject.map((s) => (
                    <div
                      key={s.subject}
                      data-testid={`subject-card-${s.subject.toLowerCase()}`}
                      className="tactile-card p-5"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <BookOpen
                            className="w-4 h-4"
                            style={{ color: scoreColor(s.avg_score) }}
                          />
                          <div className="font-bold">{s.subject}</div>
                        </div>
                        <div className="text-xs font-bold text-muted-foreground">
                          {s.attempts} {s.attempts === 1 ? "quiz" : "quizzes"}
                        </div>
                      </div>
                      <div className="h-2.5 w-full bg-muted rounded-full overflow-hidden mb-2">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${s.avg_score}%`,
                            background: scoreColor(s.avg_score),
                          }}
                        />
                      </div>
                      <div className="flex items-baseline justify-between">
                        <span className="text-xs text-muted-foreground font-semibold">
                          Avg score
                        </span>
                        <span
                          className="text-lg font-black"
                          style={{ color: scoreColor(s.avg_score) }}
                        >
                          {s.avg_score}%
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Top chapters */}
            {stats.by_topic?.length > 0 && (
              <section className="mb-10" data-testid="by-topic-section">
                <h2 className="text-xl font-black mb-4">
                  Most-practiced chapters
                </h2>
                <div className="tactile-card divide-y divide-border overflow-hidden">
                  {stats.by_topic.map((t) => (
                    <div
                      key={t.topic_id}
                      data-testid={`topic-row-${t.topic_id}`}
                      className="p-4 sm:p-5 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-bold truncate">
                          {t.topic_title || t.topic_id}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {t.subject} · Class {t.class_id} · {t.attempts}{" "}
                          {t.attempts === 1 ? "attempt" : "attempts"}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <div
                            className="text-lg font-black"
                            style={{ color: scoreColor(t.avg_score) }}
                          >
                            {t.avg_score}%
                          </div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                            avg
                          </div>
                        </div>
                        <Link
                          to={`/quiz/${t.topic_id}`}
                          data-testid={`retry-quiz-${t.topic_id}`}
                          className="pill-btn bg-foreground/5 hover:bg-foreground/10 text-xs px-3 py-1.5"
                        >
                          Retry
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Recent attempts */}
            {stats.recent?.length > 0 && (
              <section data-testid="recent-attempts-section">
                <h2 className="text-xl font-black mb-4">Recent attempts</h2>
                <div className="tactile-card divide-y divide-border overflow-hidden">
                  {stats.recent.map((a) => (
                    <div
                      key={a.id}
                      data-testid={`attempt-row-${a.id}`}
                      className="p-4 sm:p-5 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-bold truncate">
                          {a.topic_title}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {a.subject} · Class {a.class_id} ·{" "}
                          {formatDate(a.created_at)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div
                          className="text-lg font-black"
                          style={{ color: scoreColor(a.score_pct) }}
                        >
                          {a.score_pct}%
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">
                          {a.correct_count}/{a.total_questions} correct
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
